import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { userSolicitationsService } from '../../services/UserSolicitationsService';
import { UserSolicitation, SolicitationFilter } from '../../types/solicitation';
import { SolicitationCard } from './SolicitationCard';
import { analyticsService } from '../../services/analyticsService';

export const MySolicitations: React.FC = () => {
  const { currentUser } = useAuth();
  const [solicitations, setSolicitations] = useState<UserSolicitation[]>([]);
  const [filter, setFilter] = useState<SolicitationFilter>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({ active: 0, dueSoon: 0, submitted: 0 });

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.uid) return;

      setLoading(true);
      setError(null);

      try {
        const userEmail = currentUser.email || undefined;
        const [solicitationsData, statsData] = await Promise.all([
          userSolicitationsService.getUserSolicitations(currentUser.uid, filter, userEmail),
          userSolicitationsService.getSolicitationStats(currentUser.uid, userEmail),
        ]);

        setSolicitations(solicitationsData);
        setStats(statsData);
      } catch (err) {
        console.error('Error fetching solicitations:', err);
        setError('Failed to load solicitations. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser?.uid, currentUser?.email, filter]);

  const handleFilterChange = (_: React.MouseEvent<HTMLElement>, newFilter: SolicitationFilter | null) => {
    if (newFilter !== null) {
      analyticsService.filterChange('solicitation_status', newFilter);
      setFilter(newFilter);
    }
  };

  const filteredSolicitations = solicitations.filter(s => {
    if (filter === 'all') return true;
    return s.metadata.status === filter;
  });

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <AssignmentIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" fontWeight="bold">{stats.active}</Typography>
            <Typography variant="body2" color="text.secondary">Active</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: stats.dueSoon > 0 ? 'warning.light' : undefined }}>
            <WarningIcon sx={{ fontSize: 40, color: stats.dueSoon > 0 ? 'warning.dark' : 'text.secondary', mb: 1 }} />
            <Typography variant="h4" fontWeight="bold">{stats.dueSoon}</Typography>
            <Typography variant="body2" color="text.secondary">Due Soon</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" fontWeight="bold">{stats.submitted}</Typography>
            <Typography variant="body2" color="text.secondary">Submitted</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" fontWeight="600">My Solicitations</Typography>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={handleFilterChange}
          size="small"
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="submitted">Submitted</ToggleButton>
          <ToggleButton value="archived">Archived</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : filteredSolicitations.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <AssignmentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No solicitations found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filter === 'all' 
              ? 'Start tracking solicitations using the GovToolsPro Chrome extension on SAM.gov.'
              : `No ${filter} solicitations. Try a different filter.`
            }
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredSolicitations.map((solicitation) => (
            <Grid item xs={12} sm={6} md={4} key={solicitation.id}>
              <SolicitationCard solicitation={solicitation} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};
